import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "react-toastify";

import { SecondaryButton } from "@/components/common";

import { CannabinoidStorePicker } from "./CannabinoidStorePicker";
import { CheckoutAddressWizardDialog } from "./CheckoutAddressWizardDialog";
import { CheckoutPaymentOverlay } from "./CheckoutPaymentOverlay";
import { PixPaymentDialog } from "./PixPaymentDialog";
import { CheckoutReviewDialog } from "./CheckoutReviewDialog";
import { DispensaryConsentDialog } from "./DispensaryConsentDialog";
import { WooCartModal } from "./WooCartModal";
import { WooCartTriggerButton } from "./WooCartTriggerButton";
import { WooCheckoutTriggerButton } from "./WooCheckoutTriggerButton";
import { WooLoginPromptDialog } from "./WooLoginPromptDialog";
import { WooOrdersModal, type OrdersTab } from "./WooOrdersModal";
import { WooOrdersTriggerButton } from "./WooOrdersTriggerButton";
import { WooPaymentSuccessDialog } from "./WooPaymentSuccessDialog";
import { WooProductGrid } from "./WooProductGrid";
import { isStoreApiReady } from "../constants/organizationStoreRegistry";
// DEV_STORE_ASSOCIATION_BYPASS — remover import + usos ao retirar bypass (ver devStoreAssociationBypass.ts)
import {
  isDevStoreAssociationBypassAvailable,
  isDevStoreAssociationBypassEnabled,
  setDevStoreAssociationBypassEnabled,
} from "../constants/devStoreAssociationBypass";
import { useWooCommerceCart } from "../hooks/useWooCommerceCart";
import { useWooCommerceCheckout } from "../hooks/useWooCommerceCheckout";
import { useWooCommerceCustomer } from "../hooks/useWooCommerceCustomer";
import { useWooCommerceOrdersAllStores } from "../hooks/useWooCommerceOrdersAllStores";
import { woocommerceStoreService } from "../services/woocommerceStoreService";
import { useWooCommercePaymentMonitor } from "../hooks/useWooCommercePaymentMonitor";
import { useWooCommerceProducts } from "../hooks/useWooCommerceProducts";
import { useWooCommerceStores } from "../hooks/useWooCommerceStores";
import type {
  MarketplaceAddressOption,
  MarketplaceCheckoutDraft,
  WooCustomer,
  WooOrder,
  WooStoreConfig,
} from "../types";
import {
  buildAddressOptionsForCheckout,
  buildDefaultCheckoutAddresses,
  formatAddressSummary,
  isCheckoutAddressComplete,
} from "../utils/addressHelpers";
import { pharmaConsentService } from "../../services/pharmaConsentService";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";

interface CannabinoidStoreMarketplaceProps {
  userEmail: string;
  patientId?: string;
  userPatient?: unknown;
  userName?: string;
  inline?: boolean;
  hideHeader?: boolean;
  /** false = bloqueia abrir lojas; pedidos e pagamentos permanecem disponíveis. */
  canBrowseStores?: boolean;
  documentsPendingCount?: number;
  onHeaderActionsChange?: (actions: ReactNode | null) => void;
}

export function CannabinoidStoreMarketplace({
  userEmail,
  patientId = "",
  userPatient,
  userName,
  inline = true,
  hideHeader = false,
  canBrowseStores = true,
  documentsPendingCount = 0,
  onHeaderActionsChange,
}: CannabinoidStoreMarketplaceProps) {
  const theme = useTheme();

  const { stores, isConfigured, loading: storesLoading } = useWooCommerceStores();
  const [selectedStore, setSelectedStore] = useState<WooStoreConfig | null>(null);
  const [consentedInstitutionIds, setConsentedInstitutionIds] = useState<Set<string>>(
    new Set(),
  );
  const [consentDialogStore, setConsentDialogStore] = useState<WooStoreConfig | null>(
    null,
  );
  const [checkoutAddressOptions, setCheckoutAddressOptions] = useState<
    MarketplaceAddressOption[]
  >([]);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [ordersModalTab, setOrdersModalTab] = useState<OrdersTab>("unpaid");
  const [checkoutDraft, setCheckoutDraft] = useState<MarketplaceCheckoutDraft | null>(null);
  const [showCheckoutReview, setShowCheckoutReview] = useState(false);
  const [showAddressWizard, setShowAddressWizard] = useState(false);
  const [addressWizardStep, setAddressWizardStep] = useState<1 | 2>(1);
  const [addressWizardFocus, setAddressWizardFocus] = useState<
    "shipping" | "billing" | "default"
  >("default");
  const [checkoutStoreId, setCheckoutStoreId] = useState<string | null>(null);
  const [paymentOverlayOpen, setPaymentOverlayOpen] = useState(false);
  // DEV_STORE_ASSOCIATION_BYPASS — state + setter; remover com devStoreAssociationBypass.ts
  const [skipStoreAssociationCheck, setSkipStoreAssociationCheck] = useState(
    () => isDevStoreAssociationBypassEnabled(),
  );

  const {
    customer,
    status: customerStatus,
    loading: customerLoading,
    error: customerError,
    resolveCustomer,
    handleOpenIdLogin,
    refreshCustomer,
    resetCustomer,
  } = useWooCommerceCustomer(selectedStore, userEmail);

  const {
    pendingOrders,
    orders,
    loadAllOrders,
    refreshAllOrders,
    cancelOrder,
    loading: ordersLoading,
    error: ordersError,
  } = useWooCommerceOrdersAllStores(stores, userEmail);

  const resolveOrderStore = useCallback(
    (order: WooOrder) => stores.find((store) => store.id === order.storeId) ?? null,
    [stores],
  );

  const { payOrder, monitoringOrderId, successOrder, clearSuccessOrder, pixPayment, closePixPayment } =
    useWooCommercePaymentMonitor({
      store: selectedStore,
      resolveStore: resolveOrderStore,
      onOrdersRefresh: async () => {
        await refreshAllOrders();
      },
    });

  const {
    products,
    searchTerm,
    setSearchTerm,
    loadProducts,
    loading: productsLoading,
    error: productsError,
    reset: resetProducts,
  } = useWooCommerceProducts(selectedStore);

  const {
    getCartItems,
    addToCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getTotalItemsAllStores,
    getSubtotalAllStores,
    carts,
  } = useWooCommerceCart();

  const { checkoutStore: runCheckout, loading: checkoutLoading, error: checkoutError } =
    useWooCommerceCheckout();

  const storeId = selectedStore?.id ?? "";
  const subtotal = getSubtotalAllStores();
  const totalItems = getTotalItemsAllStores();

  const activeCheckoutStore = checkoutDraft?.store ?? selectedStore;
  const activeCheckoutCustomer = checkoutDraft?.customer ?? customer;

  // DEV_STORE_ASSOCIATION_BYPASS — canBrowseStoreCatalog / showLoginPrompt usam skipStoreAssociationCheck
  const canBrowseStoreCatalog =
    skipStoreAssociationCheck || customerStatus === "found";
  const showLoginPrompt =
    !skipStoreAssociationCheck && customerStatus === "login_required";

  const toggleStoreAssociationBypass = useCallback(() => {
    setSkipStoreAssociationCheck((prev) => {
      const next = !prev;
      setDevStoreAssociationBypassEnabled(next);
      toast.info(
        next
          ? "Verificação de associação na loja desligada (somente dev)."
          : "Verificação de associação na loja ligada novamente.",
      );
      return next;
    });
  }, []);

  useEffect(() => {
    if (!selectedStore) return;
    resetCustomer();
    resetProducts();
    // DEV_STORE_ASSOCIATION_BYPASS — não chama resolveCustomer quando bypass ativo
    if (skipStoreAssociationCheck) return;
    void resolveCustomer();
  }, [selectedStore?.id, skipStoreAssociationCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isConfigured) void loadAllOrders();
  }, [isConfigured, loadAllOrders]);

  useEffect(() => {
    if (!selectedStore) return;
    if (skipStoreAssociationCheck) {
      void loadProducts();
      return;
    }
    if (customerStatus === "found" && customer) {
      void loadProducts();
    }
  }, [
    skipStoreAssociationCheck,
    customerStatus,
    selectedStore?.id,
    customer?.id,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customerError) toast.error(customerError);
    if (checkoutError) toast.error(checkoutError);
    if (ordersError) toast.error(ordersError);
  }, [customerError, checkoutError, ordersError]);

  useEffect(() => {
    if (!patientId.trim()) return;

    let cancelled = false;

    (async () => {
      try {
        const consents =
          await pharmaConsentService.getActiveOrganizationConsents(patientId);
        if (!cancelled) {
          setConsentedInstitutionIds(
            new Set(consents.map((c) => c.institutionId).filter(Boolean)),
          );
        }
      } catch {
        // silencioso — badge de consentimento pode ficar impreciso
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const blockStoreBrowsing = useCallback(() => {
    toast.info(
      "Envie todos os documentos do tratamento canabinoide para acessar os dispensadores.",
    );
  }, []);

  const enterStore = useCallback((store: WooStoreConfig) => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      return;
    }
    setSelectedStore(store);
  }, [canBrowseStores, blockStoreBrowsing]);

  const handleSelectStore = useCallback(
    async (store: WooStoreConfig) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      if (!isStoreApiReady(store)) return;

      const institutionId = store.institutionId;
      if (!institutionId || !patientId.trim()) {
        enterStore(store);
        return;
      }

      if (consentedInstitutionIds.has(institutionId)) {
        enterStore(store);
        return;
      }

      try {
        const hasConsent = await pharmaConsentService.hasConsentForInstitution(
          patientId,
          institutionId,
        );
        if (hasConsent) {
          setConsentedInstitutionIds((prev) => new Set(prev).add(institutionId));
          enterStore(store);
          return;
        }
      } catch {
        toast.error("Não foi possível verificar a autorização.");
        return;
      }

      setConsentDialogStore(store);
    },
    [patientId, consentedInstitutionIds, enterStore, canBrowseStores, blockStoreBrowsing],
  );

  const handleConsentGranted = useCallback(() => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      setConsentDialogStore(null);
      return;
    }
    if (!consentDialogStore?.institutionId) return;
    setConsentedInstitutionIds((prev) =>
      new Set(prev).add(consentDialogStore.institutionId!),
    );
    const storeToEnter = consentDialogStore;
    setConsentDialogStore(null);
    enterStore(storeToEnter);
  }, [consentDialogStore, enterStore, canBrowseStores, blockStoreBrowsing]);

  const handleAddToCart = useCallback(
    (product: Parameters<typeof addToCart>[1]) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      if (!selectedStore) return;
      addToCart(selectedStore.id, product);
    },
    [canBrowseStores, blockStoreBrowsing, selectedStore, addToCart],
  );

  const resetCheckoutContext = useCallback(() => {
    setCheckoutDraft(null);
    setShowCheckoutReview(false);
    setShowAddressWizard(false);
    setAddressWizardStep(1);
    setCheckoutStoreId(null);
  }, []);

  useEffect(() => {
    if (canBrowseStores) return;
    setSelectedStore(null);
    setCartModalOpen(false);
    setConsentDialogStore(null);
    resetCheckoutContext();
    resetCustomer();
  }, [canBrowseStores, resetCheckoutContext, resetCustomer]);

  const executeCheckout = useCallback(async () => {
    if (!checkoutDraft?.addresses || !activeCheckoutStore || !activeCheckoutCustomer) {
      toast.error("Informe os endereços de entrega e cobrança antes de continuar.");
      return;
    }

    const { addresses, cartItems, store, customer: draftCustomer } = checkoutDraft;

    if (!isCheckoutAddressComplete(addresses.shipping)) {
      toast.error("Informe CPF, telefone e endereço de entrega completos.");
      return;
    }

    const billingSameAsShipping =
      formatAddressSummary(addresses.shipping) ===
      formatAddressSummary(addresses.billing);

    if (!billingSameAsShipping && !isCheckoutAddressComplete(addresses.billing)) {
      toast.error("Endereço de cobrança incompleto (CPF e telefone obrigatórios).");
      return;
    }

    try {
      setPaymentOverlayOpen(true);
      const order = await runCheckout({
        store,
        customer: draftCustomer,
        cartItems,
        userEmail,
        addresses,
      });

      clearCart(store.id);
      setCartModalOpen(false);
      resetCheckoutContext();
      void refreshAllOrders();
      await payOrder(order);
      if (store.id === selectedStore?.id) {
        await refreshCustomer();
      }
    } catch {
      // erro mapeado no hook
    } finally {
      setPaymentOverlayOpen(false);
    }
  }, [
    checkoutDraft,
    activeCheckoutStore,
    activeCheckoutCustomer,
    userEmail,
    runCheckout,
    clearCart,
    resetCheckoutContext,
    refreshAllOrders,
    payOrder,
    refreshCustomer,
    selectedStore?.id,
  ]);

  const beginCheckoutForStore = useCallback(
    async (targetStoreId: string) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      const targetStore = stores.find((store) => store.id === targetStoreId);
      if (!targetStore) return;

      const targetCartItems = getCartItems(targetStoreId);
      if (targetCartItems.length === 0) {
        toast.warning("Adicione produtos ao pedido antes de finalizar.");
        return;
      }

      setCheckoutStoreId(targetStoreId);
      setCartModalOpen(false);

      let targetCustomer: WooCustomer | null =
        targetStoreId === selectedStore?.id && customerStatus === "found"
          ? customer
          : null;

      if (!targetCustomer) {
        try {
          targetCustomer = await woocommerceStoreService.findCustomerByEmail(
            targetStore,
            userEmail,
          );
        } catch {
          toast.error("Não foi possível verificar sua conta no dispensador.");
          setCheckoutStoreId(null);
          return;
        }
      }

      if (!targetCustomer) {
        toast.error(
          "Conta não encontrada neste dispensador. Faça login primeiro.",
        );
        setCheckoutStoreId(null);
        return;
      }

      let storeOrders: WooOrder[] = [];
      try {
        storeOrders =
          await woocommerceStoreService.listOrdersByCustomerWithEscalatingWindow(
            targetStore,
            targetCustomer.id,
          );
      } catch {
        storeOrders = orders.filter((o) => o.storeId === targetStoreId);
      }

      const prefilledAddresses = buildDefaultCheckoutAddresses({
        orders: storeOrders,
        userPatient,
        userEmail,
        userName,
        patientCpf: patientId,
        customer: targetCustomer,
      });

      const addressOptions = buildAddressOptionsForCheckout({
        orders: storeOrders,
        userPatient,
        userEmail,
        userName,
        patientCpf: patientId,
      });
      setCheckoutAddressOptions(addressOptions);

      setCheckoutDraft({
        store: targetStore,
        customer: targetCustomer,
        cartItems: targetCartItems,
        addresses: prefilledAddresses,
      });
      setShowCheckoutReview(true);
    },
    [
      stores,
      getCartItems,
      selectedStore?.id,
      customer,
      customerStatus,
      userEmail,
      userPatient,
      userName,
      orders,
      canBrowseStores,
      blockStoreBrowsing,
    ],
  );

  const beginCheckout = useCallback(() => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      return;
    }
    if (!selectedStore) {
      toast.error("Selecione um dispensador para finalizar a solicitação.");
      return;
    }
    void beginCheckoutForStore(selectedStore.id);
  }, [canBrowseStores, blockStoreBrowsing, selectedStore, beginCheckoutForStore]);

  const handleQuickCheckout = useCallback(() => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      return;
    }
    if (totalItems <= 0) return;

    if (selectedStore && getCartItems(selectedStore.id).length > 0) {
      void beginCheckoutForStore(selectedStore.id);
      return;
    }

    const storeWithItems = stores.find((store) => getCartItems(store.id).length > 0);
    if (storeWithItems) {
      void beginCheckoutForStore(storeWithItems.id);
    }
  }, [canBrowseStores, blockStoreBrowsing, totalItems, selectedStore, getCartItems, stores, beginCheckoutForStore]);

  const handleOpenCart = useCallback(() => {
    setCartModalOpen(true);
  }, []);

  const handleOpenOrders = useCallback(
    (tab: OrdersTab = "unpaid") => {
      void refreshAllOrders();
      setOrdersModalTab(tab);
      setOrdersModalOpen(true);
    },
    [refreshAllOrders],
  );

  const handleAddressWizardComplete = useCallback(
    (addresses: NonNullable<MarketplaceCheckoutDraft["addresses"]>) => {
      setCheckoutDraft((prev) => (prev ? { ...prev, addresses } : prev));
      setShowAddressWizard(false);
      setShowCheckoutReview(true);
    },
    [],
  );

  const headerActions = useMemo(
    () => (
      <WooOrdersTriggerButton
        showLabel
        unpaidCount={pendingOrders.length}
        onOpenOrders={() => handleOpenOrders("unpaid")}
      />
    ),
    [pendingOrders.length, handleOpenOrders],
  );

  useEffect(() => {
    if (!onHeaderActionsChange) return;
    if (!isConfigured) {
      onHeaderActionsChange(null);
      return;
    }
    onHeaderActionsChange(headerActions);
    return () => onHeaderActionsChange(null);
  }, [headerActions, onHeaderActionsChange, isConfigured]);

  const handleCancelOrder = useCallback(
    (order: WooOrder) => {
      void cancelOrder(order);
    },
    [cancelOrder],
  );

  if (storesLoading) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography sx={{ ...mt.body, color: "text.secondary" }}>
          Carregando dispensadores...
        </Typography>
      </Box>
    );
  }

  if (!isConfigured) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.08),
          textAlign: "center",
        }}
      >
        <Typography sx={{ ...mt.body, color: "text.secondary" }}>
          Dispensação indisponível — dispensador não configurado no integrador.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: inline && !hideHeader ? 2 : 0,
        p: hideHeader ? { xs: 0, md: 0 } : { xs: 1.5, md: 2 },
        borderRadius: hideHeader ? 0 : 2,
        border: hideHeader
          ? "none"
          : `1px solid ${alpha(theme.palette.primary800.main, 0.14)}`,
        bgcolor: hideHeader
          ? "transparent"
          : alpha(theme.palette.primary800.main, 0.02),
        overflow: "hidden",
      }}
    >
      {!hideHeader ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box>
            <Typography sx={{ ...mt.pageTitle, color: "primary800.main" }}>
              Dispensação
            </Typography>
            <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
              Escolha um dispensador para solicitar produtos canabinoides.
            </Typography>
          </Box>
        </Stack>
      ) : null}

      {!canBrowseStores ? (
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(theme.palette.warning.main, 0.28),
            bgcolor: alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <Stack spacing={1.25} alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <DescriptionOutlinedIcon color="warning" fontSize="small" />
              <Typography sx={{ ...mt.sectionTitle, color: "warning.dark" }}>
                Documentos pendentes
              </Typography>
            </Stack>
            <Typography sx={{ ...mt.body, color: "text.secondary" }}>
              {documentsPendingCount > 0
                ? `Envie ${documentsPendingCount} documento(s) restante(s) na seção Documentos para acessar os dispensadores.`
                : "Envie todos os documentos do tratamento canabinoide para acessar os dispensadores."}
            </Typography>
            <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
              Você ainda pode acompanhar e pagar pedidos já criados em Meus pedidos.
            </Typography>
          </Stack>
        </Box>
      ) : !selectedStore ? (
        <Box sx={{ maxHeight: { xs: 360, md: 420 }, overflowY: "auto", pr: 0.5 }}>
          <CannabinoidStorePicker
            stores={stores}
            selectedStoreId={null}
            onSelectStore={(store) => void handleSelectStore(store)}
            consentedInstitutionIds={consentedInstitutionIds}
            variant="grid"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: { md: "calc(100vh - 280px)" },
            minHeight: { xs: 480, md: 520 },
            maxHeight: { md: "70vh" },
            overflow: "hidden",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: { md: 220 },
              flexShrink: 0,
              overflowY: "auto",
              display: { xs: "none", md: "block" },
              pr: 0.5,
            }}
          >
            <Typography
              sx={{ ...mt.sectionTitle, color: "primary800.main", display: "block", mb: 1 }}
            >
              Dispensadores
            </Typography>
            <CannabinoidStorePicker
              stores={stores}
              selectedStoreId={selectedStore.id}
              onSelectStore={(store) => void handleSelectStore(store)}
              consentedInstitutionIds={consentedInstitutionIds}
              variant="list"
            />
          </Box>

          <Stack sx={{ flex: 1, minWidth: 0, overflow: "hidden", position: "relative" }}>
            <Box sx={{ display: { xs: "flex", md: "none" }, mb: 1 }}>
              <SecondaryButton
                width="100%"
                height="44px"
                onClick={() => setSelectedStore(null)}
              >
                Trocar dispensador
              </SecondaryButton>
            </Box>

            <Typography sx={{ ...mt.sectionTitle, mb: 1 }}>{selectedStore.name}</Typography>

            {/* DEV_STORE_ASSOCIATION_BYPASS — botão dev; remover bloco inteiro ao retirar bypass */}
            {isDevStoreAssociationBypassAvailable() ? (
              <Box sx={{ mb: 1 }}>
                <Button
                  size="small"
                  variant={skipStoreAssociationCheck ? "contained" : "outlined"}
                  color={skipStoreAssociationCheck ? "warning" : "inherit"}
                  onClick={toggleStoreAssociationBypass}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  {skipStoreAssociationCheck
                    ? "Dev: associação loja OFF — clique para ligar"
                    : "Dev: pular verificação de associação na loja"}
                </Button>
              </Box>
            ) : null}

            {!skipStoreAssociationCheck &&
            (customerStatus === "loading" || customerLoading) ? (
              <Typography sx={{ ...mt.body, color: "text.secondary" }}>
                Verificando sua conta no dispensador...
              </Typography>
            ) : null}

            {canBrowseStoreCatalog ? (
              <>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: alpha(theme.palette.primary800.main, 0.02),
                    pb: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      placeholder="Buscar produtos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        sx: { ...mt.input },
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <WooCartTriggerButton
                      draftItemCount={totalItems}
                      onOpenCart={handleOpenCart}
                    />
                    <WooCheckoutTriggerButton
                      draftItemCount={totalItems}
                      loading={checkoutLoading}
                      onCheckout={handleQuickCheckout}
                    />
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, pb: 1 }}>
                  <WooProductGrid
                    products={products}
                    loading={productsLoading}
                    error={productsError}
                    onAddToCart={handleAddToCart}
                    getItemQuantity={(productId) => getItemQuantity(storeId, productId)}
                    onUpdateQuantity={(productId, qty) =>
                      updateQuantity(storeId, productId, qty)
                    }
                  />
                </Box>
              </>
            ) : null}
          </Stack>
        </Box>
      )}

      <WooCartModal
        open={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        carts={carts}
        stores={stores}
        draftSubtotal={subtotal}
        draftItemCount={totalItems}
        checkoutLoading={checkoutLoading}
        checkoutStoreId={checkoutStoreId}
        onUpdateDraftQuantity={(targetStoreId, productId, qty) =>
          updateQuantity(targetStoreId, productId, qty)
        }
        onCheckout={beginCheckout}
        onCheckoutStore={(targetStoreId) => void beginCheckoutForStore(targetStoreId)}
        onExploreProducts={() => setCartModalOpen(false)}
      />

      <WooOrdersModal
        open={ordersModalOpen}
        onClose={() => setOrdersModalOpen(false)}
        initialTab={ordersModalTab}
        orders={orders}
        stores={stores}
        loading={ordersLoading}
        monitoringOrderId={monitoringOrderId}
        onCancelOrder={handleCancelOrder}
        onPayOrder={payOrder}
        onExploreProducts={() => setOrdersModalOpen(false)}
      />

      <CheckoutPaymentOverlay
        open={
          (paymentOverlayOpen || monitoringOrderId !== null) && pixPayment === null
        }
        orderId={monitoringOrderId}
        preparing={paymentOverlayOpen && monitoringOrderId === null}
      />

      <PixPaymentDialog
        open={pixPayment !== null}
        order={pixPayment?.order ?? null}
        pixDetails={pixPayment?.details ?? null}
        monitoring={monitoringOrderId !== null}
        onClose={closePixPayment}
      />

      <WooPaymentSuccessDialog
        open={Boolean(successOrder)}
        order={successOrder}
        onClose={clearSuccessOrder}
        onViewOrders={() => {
          clearSuccessOrder();
          handleOpenOrders("paid");
        }}
      />

      <WooLoginPromptDialog
        open={showLoginPrompt}
        storeName={selectedStore?.name ?? "dispensador"}
        loading={customerLoading}
        onClose={() => {
          setSelectedStore(null);
          resetCustomer();
        }}
        onLogin={async () => {
          const resolved = await handleOpenIdLogin();
          if (!resolved) {
            toast.info(
              "Conta ainda não encontrada. Conclua o login e tente novamente.",
            );
          }
        }}
      />

      {checkoutDraft && activeCheckoutCustomer ? (
        <>
          <CheckoutReviewDialog
            open={showCheckoutReview}
            draft={checkoutDraft}
            loading={checkoutLoading}
            onClose={resetCheckoutContext}
            onEditProducts={() => {
              setShowCheckoutReview(false);
              setCartModalOpen(true);
            }}
            onEditShipping={() => {
              setShowCheckoutReview(false);
              setAddressWizardStep(1);
              setAddressWizardFocus("shipping");
              setShowAddressWizard(true);
            }}
            onEditBilling={() => {
              setShowCheckoutReview(false);
              setAddressWizardStep(2);
              setAddressWizardFocus("billing");
              setShowAddressWizard(true);
            }}
            onConfirm={() => void executeCheckout()}
          />

          <CheckoutAddressWizardDialog
            open={showAddressWizard}
            initialStep={addressWizardStep}
            wizardFocus={addressWizardFocus}
            customer={activeCheckoutCustomer}
            userEmail={userEmail}
            userPatient={userPatient}
            userName={userName}
            patientCpf={patientId}
            addressOptions={checkoutAddressOptions}
            initialShipping={checkoutDraft.addresses?.shipping}
            initialBilling={checkoutDraft.addresses?.billing}
            onClose={() => {
              setShowAddressWizard(false);
              setShowCheckoutReview(true);
            }}
            onComplete={handleAddressWizardComplete}
          />
        </>
      ) : null}

      {consentDialogStore?.institutionId ? (
        <DispensaryConsentDialog
          open
          patientId={patientId}
          dispensaryName={consentDialogStore.name}
          institutionId={consentDialogStore.institutionId}
          onClose={() => setConsentDialogStore(null)}
          onGranted={handleConsentGranted}
        />
      ) : null}
    </Stack>
  );
}
